import { DollarOutlined } from "@ant-design/icons"
import { Alert, Button, Card, Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"

import { cn } from "../../../../utils/cn.ts"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { OverviewCardTitle } from "../overview/OverviewCardIcon.tsx"

const { Text } = Typography

const PRICE_TOLERANCE = 0.005
const MILESTONE_COLOR = "#1597b1"
const OVER_BUDGET_COLOR = "#ff4d4f"
const UNDER_BUDGET_COLOR = "#faad14"

type FinancialStatus = "over" | "under" | "onBudget" | "unavailable"

interface LeadScopeMilestoneFinancialSummaryProps {
  salesPrice?: number | string | null
  totalMilestones: number
  hasMilestones: boolean
  onReviewMilestones?: () => void
}

const formatSignedBudget = (value: number) => {
  if (Math.abs(value) <= PRICE_TOLERANCE) return budgetFormat(0)

  return `${value > 0 ? "+" : "-"}${budgetFormat(Math.abs(value))}`
}

const formatSignedPercent = (value: number) =>
  `${value > 0 ? "+" : value < 0 ? "-" : ""}${Math.abs(value).toFixed(2)}%`

const financialStatus = (
  totalMilestones: number,
  salesPrice: number,
  hasMilestones: boolean
): FinancialStatus => {
  if (!hasMilestones || !Number.isFinite(salesPrice) || salesPrice <= 0) {
    return "unavailable"
  }

  const variance = totalMilestones - salesPrice
  if (variance > PRICE_TOLERANCE) return "over"
  if (variance < -PRICE_TOLERANCE) return "under"
  return "onBudget"
}

function LeadScopeMilestoneFinancialSummary({
  salesPrice,
  totalMilestones,
  hasMilestones,
  onReviewMilestones
}: LeadScopeMilestoneFinancialSummaryProps) {
  const { t } = useTranslation()
  const parsedSalesPrice = Number(salesPrice)
  const hasSalesPrice =
    Number.isFinite(parsedSalesPrice) && parsedSalesPrice > 0
  const status = financialStatus(
    totalMilestones,
    parsedSalesPrice,
    hasMilestones
  )
  const comparable = status !== "unavailable"
  const variance = comparable ? totalMilestones - parsedSalesPrice : 0
  const variancePercent = comparable
    ? (variance / parsedSalesPrice) * 100
    : 0
  const totalPercent = comparable
    ? (totalMilestones / parsedSalesPrice) * 100
    : 0
  const mismatch = status === "over" || status === "under"
  const deltaColor = status === "under" ? UNDER_BUDGET_COLOR : OVER_BUDGET_COLOR
  const ringBasePercent = comparable
    ? (Math.min(totalMilestones, parsedSalesPrice) /
        Math.max(totalMilestones, parsedSalesPrice)) *
      100
    : totalMilestones > 0
      ? 100
      : 0
  const ringBackground =
    ringBasePercent >= 100 || !mismatch
      ? `conic-gradient(${MILESTONE_COLOR} 0 100%)`
      : `conic-gradient(${MILESTONE_COLOR} 0 ${ringBasePercent}%, ${deltaColor} ${ringBasePercent}% 100%)`

  const statusLabel =
    status === "over"
      ? t("leadWorkspace.scope.milestones.financialSummary.over")
      : status === "under"
        ? t("leadWorkspace.scope.milestones.financialSummary.under")
        : status === "onBudget"
          ? t("leadWorkspace.scope.milestones.financialSummary.onBudget")
          : ""

  const alertTitle =
    status === "over"
      ? t("leadWorkspace.scope.milestones.financialSummary.overBudget")
      : t("leadWorkspace.scope.milestones.financialSummary.underBudget")
  const alertDescription =
    status === "over"
      ? t("leadWorkspace.scope.milestones.financialSummary.overBudgetDescription", {
          amount: budgetFormat(Math.abs(variance)),
          percent: `${Math.abs(variancePercent).toFixed(2)}%`
        })
      : t("leadWorkspace.scope.milestones.financialSummary.underBudgetDescription", {
          amount: budgetFormat(Math.abs(variance)),
          percent: `${Math.abs(variancePercent).toFixed(2)}%`
        })

  return (
    <Card
      size={"small"}
      title={
        <OverviewCardTitle icon={<DollarOutlined />} accent={"green"}>
          {t("leadWorkspace.scope.milestones.financialSummary.title")}
        </OverviewCardTitle>
      }
      data-testid={"milestones-financial-summary"}
    >
      <div
        className={cn(
          "grid gap-4",
          totalMilestones > 0 &&
            "xl:grid-cols-[minmax(0,2fr)_minmax(200px,1fr)_minmax(260px,1.1fr)]"
        )}
      >
        <div className={"grid grid-cols-2 gap-3 md:grid-cols-4"}>
          <SummaryMetric
            label={t(
              "leadWorkspace.scope.milestones.financialSummary.salesPrice"
            )}
            value={hasSalesPrice ? budgetFormat(parsedSalesPrice) : "—"}
          />
          <SummaryMetric
            label={t(
              "leadWorkspace.scope.milestones.financialSummary.totalMilestones"
            )}
            value={budgetFormat(totalMilestones)}
          />
          <SummaryMetric
            label={t(
              "leadWorkspace.scope.milestones.financialSummary.variance"
            )}
            value={comparable ? formatSignedBudget(variance) : "—"}
            status={status}
            statusLabel={statusLabel}
            testId={"milestones-financial-variance"}
          />
          <SummaryMetric
            label={t(
              "leadWorkspace.scope.milestones.financialSummary.variancePercent"
            )}
            value={comparable ? formatSignedPercent(variancePercent) : "—"}
            status={status}
            statusLabel={statusLabel}
            testId={"milestones-financial-variance-percent"}
          />
        </div>

        {/* The ring only earns its space once there's a real total to show —
            zero milestones, or milestones that are all still $0 (a lead with
            rows added but not yet priced), both draw the same permanently
            "full" teal circle, which read as decoration rather than a chart
            (2026-08-30). */}
        {totalMilestones > 0 && (
          <div className={"flex items-center gap-3"}>
            <div
              className={"h-16 w-16 shrink-0 rounded-full p-2"}
              data-testid={"milestones-financial-donut"}
              role={"img"}
              aria-label={t(
                "leadWorkspace.scope.milestones.financialSummary.donutAria",
                { percent: comparable ? `${totalPercent.toFixed(2)}%` : "—" }
              )}
              style={{ background: ringBackground }}
            >
              <div
                className={"flex h-full w-full items-center justify-center rounded-full"}
                style={{
                  background: "var(--ant-color-bg-container, #ffffff)"
                }}
              />
            </div>
            <Flex vertical gap={"small"} className={"min-w-0 text-xs"}>
              <FinancialLegend
                color={MILESTONE_COLOR}
                label={t(
                  "leadWorkspace.scope.milestones.financialSummary.milestones"
                )}
                value={budgetFormat(totalMilestones)}
                percent={comparable ? `${totalPercent.toFixed(2)}%` : "—"}
              />
              <FinancialLegend
                color={mismatch ? deltaColor : MILESTONE_COLOR}
                label={
                  mismatch
                    ? status === "over"
                      ? t(
                          "leadWorkspace.scope.milestones.financialSummary.overBudget"
                        )
                      : t(
                          "leadWorkspace.scope.milestones.financialSummary.underBudget"
                        )
                    : t(
                        "leadWorkspace.scope.milestones.financialSummary.onBudget"
                      )
                }
                value={
                  mismatch ? budgetFormat(Math.abs(variance)) : budgetFormat(0)
                }
                percent={
                  mismatch ? `${Math.abs(variancePercent).toFixed(2)}%` : "0.00%"
                }
              />
            </Flex>
          </div>
        )}

        {mismatch && (
          <Alert
            showIcon
            type={status === "over" ? "error" : "warning"}
            data-testid={"milestones-sales-price-alert"}
            message={alertTitle}
            description={alertDescription}
            action={
              <Button
                size={"small"}
                type={"default"}
                onClick={onReviewMilestones}
              >
                {t("leadWorkspace.scope.milestones.financialSummary.review")}
              </Button>
            }
          />
        )}
      </div>
    </Card>
  )
}

interface SummaryMetricProps {
  label: string
  value: string
  status?: FinancialStatus
  statusLabel?: string
  testId?: string
}

function SummaryMetric({
  label,
  value,
  status,
  statusLabel,
  testId
}: SummaryMetricProps) {
  const textType =
    status === "over"
      ? "danger"
      : status === "under"
        ? "warning"
        : undefined

  return (
    <Flex
      vertical
      gap={4}
      className={"border-r border-border pr-3 last:border-r-0"}
    >
      <Text type={"secondary"} className={"text-xs"}>
        {label}
      </Text>
      <Text strong type={textType} className={"text-base"} data-testid={testId}>
        {value}
      </Text>
      {statusLabel && status !== "unavailable" && (
        <Text type={textType} className={"text-xs"}>{`(${statusLabel})`}</Text>
      )}
    </Flex>
  )
}

interface FinancialLegendProps {
  color: string
  label: string
  value: string
  percent: string
}

function FinancialLegend({ color, label, value, percent }: FinancialLegendProps) {
  return (
    <div className={"flex items-start gap-2"}>
      <span
        className={"mt-1.5 h-2 w-2 shrink-0 rounded-full"}
        style={{ backgroundColor: color }}
      />
      <Text>
        {label} {value} ({percent})
      </Text>
    </div>
  )
}

export { LeadScopeMilestoneFinancialSummary }
